import { VijiCoreError, HostToWorkerMessage, WorkerToHostMessage } from '../types/index.js';
import VijiWorker from './viji.worker.ts?worker&module';

/**
 * Manages the WebWorker that executes artist scene code.
 * Provides secure sandboxed execution with controlled API access.
 */
export class WorkerManager {
  private worker: Worker | null = null;
  private messageId = 0;
  private pendingMessages = new Map<string, {
    resolve: (value: any) => void;
    reject: (error: any) => void;
    timeout: ReturnType<typeof setTimeout>;
  }>();
  private messageHandlers = new Map<string, (data: any) => void>();
  private isInitialized = false;

  constructor(
    private sceneCode: string,
    private offscreenCanvas: OffscreenCanvas
  ) {}

  /**
   * Creates and initializes the WebWorker with artist code
   */
  async createWorker(): Promise<void> {
    try {
      // Create worker using Vite's worker import (hashed asset, correct MIME)
      this.worker = new VijiWorker();
      
      // Setup message handling
      this.setupMessageHandling();
      
      // Transfer OffscreenCanvas to worker and wait for init response
      // IMPORTANT: Canvas must be initialized BEFORE scene code to avoid race conditions
      await this.initializeWorker();
      
      // Send scene code to worker (after canvas is ready)
      this.postMessage('set-scene-code', { sceneCode: this.sceneCode });
      
      this.isInitialized = true;
    } catch (error) {
      throw new VijiCoreError(
        `Failed to create worker: ${error}`,
        'WORKER_CREATION_ERROR',
        { error }
      );
    }
  }

  /**
   * Sends a message to the worker and returns a promise for the response
   */
  async sendMessage(type: string, data?: any, timeout = 5000): Promise<any> {
    if (!this.worker) {
      throw new VijiCoreError('Worker not initialized', 'WORKER_NOT_READY');
    }

    const id = `msg_${++this.messageId}`;
    const message: HostToWorkerMessage = {
      type: type as any,
      id,
      timestamp: Date.now(),
      data
    };

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingMessages.delete(id);
        reject(new VijiCoreError(`Message timeout: ${type}`, 'MESSAGE_TIMEOUT'));
      }, timeout);

      this.pendingMessages.set(id, {
        resolve,
        reject,
        timeout: timeoutId
      });

      this.worker!.postMessage(message);
    });
  }

  /**
   * Sends a message without waiting for response (fire and forget)
   */
  postMessage(type: string, data?: any): void;
  postMessage(type: string, data: any, transfer: Transferable[]): void;
  postMessage(type: string, data?: any, transfer?: Transferable[]): void {
    if (!this.worker) {
      console.warn('Attempted to post message to uninitialized worker');
      return;
    }

    const message: HostToWorkerMessage = {
      type: type as any,
      id: `fire_${++this.messageId}`,
      timestamp: Date.now(),
      data
    };

    if (transfer && transfer.length > 0) {
      this.worker.postMessage(message, transfer);
    } else {
      this.worker.postMessage(message);
    }
  }


  /**
   * Registers a handler for worker messages
   */
  onMessage(type: string, handler: (data: any) => void): void {
    this.messageHandlers.set(type, handler);
  }

  /**
   * Removes a message handler
   */
  offMessage(type: string): void {
    this.messageHandlers.delete(type);
  }

  /**
   * Terminates the worker and cleans up resources
   */
  destroy(): void {
    try {
      // Clear pending messages
      this.pendingMessages.forEach(({ timeout, reject }) => {
        clearTimeout(timeout);
        reject(new VijiCoreError('Worker destroyed', 'WORKER_DESTROYED'));
      });
      this.pendingMessages.clear();

      // Clear message handlers
      this.messageHandlers.clear();

      // Terminate worker
      if (this.worker) {
        this.worker.terminate();
        this.worker = null;
      }

      this.isInitialized = false;
    } catch (error) {
      console.warn('Error during worker cleanup:', error);
    }
  }

  /**
   * Checks if worker is ready for use
   */
  get ready(): boolean {
    return this.isInitialized && this.worker !== null;
  }

  /**
   * Gets the worker instance
   */
  get instance(): Worker | null {
    return this.worker;
  }

  /**
   * Sets up message handling for worker communication
   */
  private setupMessageHandling(): void {
    if (!this.worker) return;

    this.worker.onmessage = (event) => {
      const message: WorkerToHostMessage = event.data;
      
      // Handle pending message responses
      if (this.pendingMessages.has(message.id)) {
        const pending = this.pendingMessages.get(message.id)!;
        clearTimeout(pending.timeout);
        this.pendingMessages.delete(message.id);
        
        if (message.type === 'error') {
          pending.reject(new VijiCoreError(
            message.data?.message || 'Worker error',
            message.data?.code || 'WORKER_ERROR',
            message.data
          ));
        } else {
          pending.resolve(message.data);
        }
        return;
      }

      // Handle event messages
      const handler = this.messageHandlers.get(message.type);
      if (handler) {
        try {
          handler(message.data);
        } catch (error) {
          console.error(`Error in message handler for ${message.type}:`, error);
        }
      }
    };

    this.worker.onerror = (error) => {
      console.error('Worker error:', error);
      
      // Reject all pending messages
      this.pendingMessages.forEach(({ timeout, reject }) => {
        clearTimeout(timeout);
        reject(new VijiCoreError('Worker error', 'WORKER_ERROR', error));
      });
      this.pendingMessages.clear();
    };
  }

  /**
   * Initializes the worker with canvas and basic setup
   * Waits for init-response message (worker will also send separate 'ready' event)
   */
  private async initializeWorker(): Promise<void> {
    if (!this.worker) {
      throw new VijiCoreError('Worker not created', 'WORKER_NOT_CREATED');
    }

    // Create message for canvas transfer
    const id = `msg_${++this.messageId}`;
    const message: HostToWorkerMessage = {
      type: 'init',
      id,
      timestamp: Date.now(),
      data: {
        canvas: this.offscreenCanvas
      }
    };

    // Transfer OffscreenCanvas (not clone) - this is critical!
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingMessages.delete(id);
        reject(new VijiCoreError('Canvas transfer timeout', 'CANVAS_TRANSFER_TIMEOUT'));
      }, 10000);

      this.pendingMessages.set(id, {
        resolve,
        reject,
        timeout: timeoutId
      });

      // Transfer the canvas using the transferable objects parameter
      this.worker!.postMessage(message, [this.offscreenCanvas]);
    });
  }


} 